import { getMeilisearchClient } from '@/lib/api/meilisearch-client';
import type { Meilisearch_SousUnivers } from '@/lib/api/meilisearch-types';
import { logger } from '@/logger';

// Une recherche Meili plafonne à 1000 résultats (maxTotalHits par défaut) ;
// l'index compte 137 sous-univers au 2026-10-29.
const SUB_UNIVERSES_LIMIT = 1000;

// Champs lus par l'arbre (buildUniverseTree), les libellés
// (formatSubUniverseLabel) et le menu du filtre Univers (useGroupedUniverses,
// HierarchicalDropdownResults).
const SUB_UNIVERSE_FIELDS = [
  'id',
  'hierarchical_value',
  'fr.nom_sous_univers',
  'en.nom_sous_univers',
  'fr.slug',
  'en.slug',
  'univers.fr.nom',
  'univers.en.nom',
  'univers.fr.slug',
  'univers.en.slug',
];

/**
 * Charge TOUS les sous-univers, de quoi bâtir l'arbre des univers et les
 * libellés des sous-univers. Une liste tronquée ferait passer un univers
 * incomplet pour complet : on préfère alors ne rien regrouper.
 *
 * @returns Les sous-univers, ou une liste vide (erreur tracée) en cas d'échec
 */
export async function fetchSubUniverseCatalog(): Promise<
  Meilisearch_SousUnivers[]
> {
  try {
    const { hits, estimatedTotalHits } = await getMeilisearchClient()
      .index<Meilisearch_SousUnivers>('sous_univers')
      .search('', {
        limit: SUB_UNIVERSES_LIMIT,
        attributesToRetrieve: SUB_UNIVERSE_FIELDS,
      });
    // Meili plafonne estimatedTotalHits à maxTotalHits (1000) : une page
    // pleine peut en cacher d'autres, on la tient donc pour tronquée.
    if (
      hits.length >= SUB_UNIVERSES_LIMIT ||
      (estimatedTotalHits ?? 0) > hits.length
    ) {
      logger.error(
        { received: hits.length, estimatedTotalHits },
        'fetchSubUniverseCatalog: truncated sub-universe list, universes are not grouped',
      );
      return [];
    }
    return hits;
  } catch (error) {
    logger.error(
      { err: error },
      'fetchSubUniverseCatalog: sub-universes unavailable, universes are not grouped',
    );
    return [];
  }
}

// Chargement partagé côté navigateur : le menu Univers et l'arbre de repli des
// alertes lisent la même liste, chargée une seule fois par page. Jamais appelé
// au rendu serveur (le cache vivrait d'une requête à l'autre).
let catalogPromise: Promise<Meilisearch_SousUnivers[]> | null = null;

/**
 * Catalogue des sous-univers côté navigateur, chargé une fois et partagé ; un
 * échec (liste vide) est retenté à l'appel suivant.
 */
export function loadSubUniverseCatalog(): Promise<Meilisearch_SousUnivers[]> {
  if (!catalogPromise) {
    catalogPromise = fetchSubUniverseCatalog().then((list) => {
      if (list.length === 0) catalogPromise = null;
      return list;
    });
  }
  return catalogPromise;
}

/** Oublie le catalogue chargé (tests uniquement). */
export function resetSubUniverseCatalogForTests(): void {
  catalogPromise = null;
}
